import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Trash2, AlertTriangle, Search, Loader2, History, HardDrive, FileText, FolderOpen } from 'lucide-react';

export default function PurgePage() {
  const [months, setMonths] = useState('6');
  const [customMode, setCustomMode] = useState(false);
  const [customValue, setCustomValue] = useState('1');
  const [customUnit, setCustomUnit] = useState('months');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PAGE_SIZE = 13;
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [executing, setExecuting] = useState(false);
  const [warningPulsing, setWarningPulsing] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/super/purge/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setHistory(data.data || []);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchPreview = async () => {
    const m = parseFloat(months);
    if (!m || m <= 0) {
      toast.error('Período inválido');
      return;
    }
    setLoadingPreview(true);
    setPreview(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/super/purge/preview?months=${m}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setPreview(data);
    } catch (error) {
      toast.error('Erro ao calcular preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Função para tocar som de aviso "warning/erro"
  const playWarningSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Criar reverb simples com delay
      const createReverb = () => {
        const convolver = audioContext.createConvolver();
        const rate = audioContext.sampleRate;
        const length = rate * 0.7;
        const impulse = audioContext.createBuffer(2, length, rate);
        
        for (let channel = 0; channel < 2; channel++) {
          const channelData = impulse.getChannelData(channel);
          for (let i = 0; i < length; i++) {
            channelData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 2.5);
          }
        }
        convolver.buffer = impulse;
        return convolver;
      };
      
      const reverb = createReverb();
      const reverbGain = audioContext.createGain();
      reverbGain.gain.value = 0.5;
      
      reverb.connect(reverbGain);
      reverbGain.connect(audioContext.destination);
      
      // Som de erro - 2 notas agudas, pausa, 2 notas agudas
      const notes = [
        { freq: 880, start: 0, duration: 0.15 },
        { freq: 880, start: 0.20, duration: 0.15 },
        { freq: 880, start: 0.85, duration: 0.15 },
        { freq: 880, start: 1.05, duration: 0.18 },
      ];
      
      notes.forEach(note => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        gainNode.connect(reverb);
        
        oscillator.frequency.value = note.freq;
        oscillator.type = 'square';
        
        const startTime = audioContext.currentTime + note.start;
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + note.duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + note.duration);
      });
    } catch (e) {
      console.log('Audio não suportado:', e);
    }
  };

  const handleOpenConfirm = () => {
    setConfirmText('');
    setConfirmDialogOpen(true);
    setWarningPulsing(true);
    playWarningSound();
    setTimeout(() => setWarningPulsing(false), 1300);
  };

  const handleExecutePurge = async () => {
    if (confirmText !== 'PURGAR') {
      toast.error('Digite PURGAR para confirmar');
      return;
    }

    setExecuting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/super/purge?months=${months}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Erro ao executar purga');
      }
      
      const data = await response.json();
      toast.success(`Purga concluída! ${data.messages_deleted} mensagens e ${data.files_deleted} arquivos removidos.`);
      setConfirmDialogOpen(false);
      setPreview(null);
      setConfirmText('');
      fetchHistory();
    } catch (error) {
      toast.error('Erro ao executar purga');
    } finally {
      setExecuting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="purge-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Purga de Dados</h1>
        <p className="text-muted-foreground">
          Remova permanentemente mensagens antigas do histórico e arquivos de anexos para liberar espaço.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:max-h-[360px]">
      {/* Card Principal */}
      <Card className="flex flex-col overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Trash2 className="w-5 h-5" />
            Liberar Espaço
          </CardTitle>
          <CardDescription>
            Selecione o período para purgar mensagens e anexos antigos.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 flex-1 overflow-auto min-h-0">
          {/* Seleção de Período */}
          <div className="flex items-start gap-4">
            <div className="space-y-2 flex-1 max-w-lg">
              <Label>Período de Retenção</Label>
              <div className="flex items-center gap-4">
                <Select value={customMode ? 'custom' : months === '99999' ? 'all' : months} onValueChange={(val) => {
                  if (val === 'custom') {
                    setCustomMode(true);
                    const v = parseFloat(customValue) || 1;
                    if (customUnit === 'days') setMonths(String(v / 30));
                    else if (customUnit === 'weeks') setMonths(String((v * 7) / 30));
                    else setMonths(String(v));
                  } else if (val === 'all') {
                    setCustomMode(false);
                    setMonths('99999');
                  } else {
                    setCustomMode(false);
                    setMonths(val);
                  }
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o período" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">Manter últimos 3 meses</SelectItem>
                    <SelectItem value="6">Manter últimos 6 meses</SelectItem>
                    <SelectItem value="12">Manter últimos 12 meses</SelectItem>
                    <SelectItem value="custom">Personalizado</SelectItem>
                    <SelectItem value="all" className="text-red-600 font-bold">APAGAR TUDO</SelectItem>
                  </SelectContent>
                </Select>
                {customMode && (
                  <>
                    <Input
                      type="number"
                      min="1"
                      value={customValue}
                      onChange={(e) => {
                        setCustomValue(e.target.value);
                        const v = parseFloat(e.target.value) || 1;
                        if (customUnit === 'days') setMonths(String(v / 30));
                        else if (customUnit === 'weeks') setMonths(String((v * 7) / 30));
                        else setMonths(String(v));
                      }}
                      className="w-20"
                    />
                    <Select value={customUnit} onValueChange={(unit) => {
                      setCustomUnit(unit);
                      const v = parseFloat(customValue) || 1;
                      if (unit === 'days') setMonths(String(v / 30));
                      else if (unit === 'weeks') setMonths(String((v * 7) / 30));
                      else setMonths(String(v));
                    }}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="days">Dias</SelectItem>
                        <SelectItem value="weeks">Semanas</SelectItem>
                        <SelectItem value="months">Meses</SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                )}
                <Button 
                  onClick={fetchPreview} 
                  disabled={loadingPreview}
                  className="shrink-0"
                >
                  {loadingPreview ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4 mr-2" />
                  )}
                  Calcular Purga
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Apenas anexos físicos serão removidos. Mensagens e campanhas são preservadas.
              </p>
            </div>
          </div>

          {/* Preview */}
          {preview && (
            <div className="border rounded-lg p-3 bg-amber-50 border-amber-200">
              <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Preview - Anexos que serão removidos
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-lg p-3 border border-amber-200 text-center">
                  <FolderOpen className="w-5 h-5 text-amber-700 mx-auto mb-1" />
                  <span className="text-sm font-medium text-amber-700">Arquivos</span>
                  <p className="text-2xl font-bold text-amber-900 mt-0.5">{preview.files_count.toLocaleString()}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-amber-200 text-center">
                  <HardDrive className="w-5 h-5 text-amber-700 mx-auto mb-1" />
                  <span className="text-sm font-medium text-amber-700">Espaço a liberar</span>
                  <p className="text-2xl font-bold text-amber-900 mt-0.5">{preview.files_size_formatted}</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-amber-200 text-center">
                  <FileText className="w-5 h-5 text-amber-700 mx-auto mb-1" />
                  <span className="text-sm font-medium text-amber-700">Mensagens afetadas</span>
                  <p className="text-2xl font-bold text-amber-900 mt-0.5">{(preview.messages_with_attachments || 0).toLocaleString()}</p>
                  <p className="text-xs text-amber-600">anexos marcados como removidos</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-amber-200 text-center">
                  <AlertTriangle className="w-5 h-5 text-amber-700 mx-auto mb-1" />
                  <span className="text-sm font-medium text-amber-700">Campanhas afetadas</span>
                  <p className="text-2xl font-bold text-amber-900 mt-0.5">{preview.campaigns_affected}</p>
                  <p className="text-xs text-amber-600">serão marcadas "anexo expirado"</p>
                </div>
              </div>
              <p className="text-xs text-amber-600 mt-2">
                Data de corte: {formatDate(preview.cutoff_date)}. Mensagens e campanhas serão preservadas.
              </p>

              {/* Botão de Executar */}
              <div className="mt-3 pt-3 border-t border-amber-200">
                <Button 
                  onClick={handleOpenConfirm}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={preview.files_count === 0}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Executar Purga
                </Button>
                {preview.files_count === 0 && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Não há anexos para purgar neste período
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Histórico de Purgas */}
      <Card className="flex flex-col overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Histórico de Purgas
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 flex-1 flex flex-col min-h-0">
          {loadingHistory ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhuma purga realizada ainda</p>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-auto min-h-0">
                <Table>
                  <TableHeader className="sticky top-0 bg-card z-10">
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Executado por</TableHead>
                      <TableHead>Período</TableHead>
                      <TableHead className="text-right">Arquivos</TableHead>
                      <TableHead className="text-right">Espaço Liberado</TableHead>
                      <TableHead className="text-right">Campanhas</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history
                      .slice((historyPage - 1) * HISTORY_PAGE_SIZE, historyPage * HISTORY_PAGE_SIZE)
                      .map((item) => (
                        <TableRow key={item._id}>
                          <TableCell className="font-medium">
                            {formatDate(item.executed_at)}
                          </TableCell>
                          <TableCell>{item.executed_by_name}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">
                              {item.months >= 9999 ? 'TUDO' : item.months < 1 ? `${Math.round(item.months * 30)} dias` : `${item.months} meses`}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {(item.files_deleted || 0).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            {item.size_freed_formatted}
                          </TableCell>
                          <TableCell className="text-right">
                            {item.campaigns_marked_expired || 0}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
              {/* Paginação */}
              <div className="flex items-center justify-between px-4 py-2 border-t bg-card text-xs">
                <span className="text-muted-foreground" data-testid="history-pagination-info">
                  Mostrando {(historyPage - 1) * HISTORY_PAGE_SIZE + 1}–{Math.min(historyPage * HISTORY_PAGE_SIZE, history.length)} de {history.length}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2"
                    onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                    disabled={historyPage === 1}
                    data-testid="history-prev-btn"
                  >
                    Anterior
                  </Button>
                  <span className="text-muted-foreground">
                    Página {historyPage} de {Math.max(1, Math.ceil(history.length / HISTORY_PAGE_SIZE))}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2"
                    onClick={() => setHistoryPage((p) => Math.min(Math.ceil(history.length / HISTORY_PAGE_SIZE), p + 1))}
                    disabled={historyPage >= Math.ceil(history.length / HISTORY_PAGE_SIZE)}
                    data-testid="history-next-btn"
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      </div>

      {/* Modal de Confirmação */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="border-red-300">
          <DialogHeader>
            <DialogTitle className="text-red-600 text-xl flex items-center gap-2">
              <AlertTriangle
                className={`w-8 h-8 fill-red-600 text-white ${warningPulsing ? 'animate-pulse' : ''}`}
                style={warningPulsing ? {
                  animation: 'warningPulse 0.2s ease-in-out infinite',
                } : {}}
              />
              <span className="font-bold">ATENÇÃO!</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-semibold mb-3">
                Esta ação é IRREVERSÍVEL!
              </p>
              <p className="text-red-700 text-sm mb-3">
                Serão removidos permanentemente:
              </p>
              <ul className="text-red-700 text-sm space-y-1 ml-4">
                <li>• <strong>{preview?.files_count.toLocaleString()}</strong> arquivos de anexo ({preview?.files_size_formatted})</li>
                <li>• <strong>{preview?.campaigns_affected}</strong> campanhas marcadas como "anexo expirado"</li>
              </ul>
              <p className="text-red-600 text-xs mt-3 font-medium">
                Mensagens e campanhas serão preservadas no histórico.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm" className="text-red-700 font-semibold">
                Digite PURGAR para confirmar:
              </Label>
              <Input
                id="confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                placeholder="PURGAR"
                className="border-red-300 focus:border-red-500 focus:ring-red-500 text-center font-bold text-lg"
                autoComplete="off"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline"
              onClick={() => setConfirmDialogOpen(false)}
              disabled={executing}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleExecutePurge}
              disabled={confirmText !== 'PURGAR' || executing}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {executing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Executando...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  CONFIRMAR PURGA
                </>
              )}
            </Button>
          </DialogFooter>

          <style>{`
            @keyframes warningPulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.3); }
            }
          `}</style>
        </DialogContent>
      </Dialog>
    </div>
  );
}
